<!-- ImprovedStatsTile.vue -->
<template>
  <div class="stat-tile" :class="{ 'is-loading': loading }">
    <!-- Decorative Element -->
    <div class="tile-decoration"></div>

    <!-- Loading Skeleton -->
    <div v-if="loading" class="tile-skeleton">
      <div class="skeleton-header"></div>
      <div class="skeleton-metrics">
        <div class="skeleton-metric"></div>
        <div class="skeleton-metric"></div>
      </div>
      <div class="skeleton-bar"></div>
      <div class="skeleton-button"></div>
    </div>

    <!-- Actual Content -->
    <div v-else class="tile-content">
      <!-- Tile Header -->
      <div class="tile-header">
        <h3 class="tile-title">{{ areaTitle }}</h3>
        <div
          class="tile-icon"
          :title="`${areaTitle} - ${totalPolicies} total policies`"
        >
          <img
            v-if="iconSrc"
            :src="iconSrc"
            :alt="areaTitle"
            class="icon-image"
          />
          <span v-else class="icon-fallback">📊</span>
        </div>
      </div>

      <!-- Metrics Section -->
      <div class="tile-metrics">
        <div class="metric">
          <div class="metric-label">Passing</div>
          <div class="metric-value passing">{{ policyCounts.totalPasses }}</div>
          <div class="metric-sublabel">Policies</div>
        </div>
        <div class="metric">
          <div class="metric-label">Failing</div>
          <div class="metric-value failing">{{ policyCounts.totalFails }}</div>
          <div class="metric-sublabel">Policies</div>
        </div>
      </div>

      <!-- Status Badge -->
      <div :class="['status-badge-bottom', statusClass]">
        {{ statusLabel }}
      </div>

      <!-- Progress Bar -->
      <div class="tile-progress">
        <div class="compliance-score">
          <span class="score-value">{{ compliancePercentage }}%</span>
          <span class="score-label">Compliance</span>
        </div>
        <div class="progress-bar">
          <div
            v-if="compliancePercentage === 0"
            class="progress-fill progress-poor"
            style="
              width: 100%;
              background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
            "
          ></div>
          <div
            v-else
            class="progress-fill"
            :class="progressBarClass"
            :style="{
              width: compliancePercentage + '%',
              background: progressBarGradient,
            }"
          ></div>
        </div>
      </div>

      <!-- Action Button -->
      <button class="tile-action" @click="handleMoreInfo">
        View Details
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 12L10 8L6 4"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "ImprovedStatsTile",
  props: {
    areaTitle: {
      type: String,
      required: true,
    },
    iconSrc: {
      type: String,
      default: null,
    },
    policyCounts: {
      type: Object,
      required: true,
      validator: (value) => {
        return (
          typeof value.totalPasses === "number" &&
          typeof value.totalFails === "number"
        );
      },
    },
    passingPolicies: {
      type: Array,
      default: () => [],
    },
    failingPolicies: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    totalPolicies() {
      return this.policyCounts.totalPasses + this.policyCounts.totalFails;
    },
    compliancePercentage() {
      if (this.totalPolicies === 0) return 0;
      return (
        Math.round(
          (this.policyCounts.totalPasses / this.totalPolicies) * 100 * 10
        ) / 10
      );
    },
    statusClass() {
      if (this.policyCounts.totalFails === 0) return "all-pass";
      if (this.policyCounts.totalPasses === 0) return "has-fails";
      return "mixed";
    },
    statusLabel() {
      if (this.policyCounts.totalFails === 0) return "All Pass";
      if (this.policyCounts.totalPasses === 0) return "Needs Attention";
      return "Mixed";
    },
    progressBarClass() {
      if (this.compliancePercentage >= 80) return "progress-excellent";
      if (this.compliancePercentage >= 50) return "progress-moderate";
      return "progress-poor";
    },
    progressBarGradient() {
      if (this.compliancePercentage >= 80) {
        return "linear-gradient(90deg, #10b981 0%, #059669 100%)";
      } else if (this.compliancePercentage >= 50) {
        return "linear-gradient(90deg, #f59e0b 0%, #d97706 100%)";
      }
      return "linear-gradient(90deg, #ef4444 0%, #dc2626 100%)";
    },
  },
  methods: {
    handleMoreInfo() {
      this.$emit("more-info", {
        areaTitle: this.areaTitle,
        passingPolicies: this.passingPolicies,
        failingPolicies: this.failingPolicies,
      });
    },
  },
};
</script>

<style scoped>
/* Individual Stat Tile */
.stat-tile {
  background: #ffffff;
  border-radius: 0.75rem;
  padding: 1.5rem;
  position: relative;
  border: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  min-height: 280px;
}

.stat-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px -10px rgba(0, 0, 0, 0.1);
  border-color: #cbd5e1;
}

.stat-tile.is-loading {
  pointer-events: none;
}

/* Loading Skeleton */
.tile-skeleton {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.skeleton-header {
  height: 3rem;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.5rem;
  margin-bottom: 1.25rem;
}

.skeleton-metrics {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.25rem;
}

.skeleton-metric {
  flex: 1;
  height: 4rem;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.5rem;
}

.skeleton-bar {
  height: 2.5rem;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
}

.skeleton-button {
  height: 2.5rem;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.375rem;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Tile Content */
.tile-content {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Tile Header */
.tile-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.25rem;
}

.tile-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.5;
  flex: 1;
  padding-right: 0.5rem;
}

/* Status Icon */
.tile-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  padding: 0.5rem;
  flex-shrink: 0;
  cursor: help;
  transition: transform 0.2s ease;
}

.tile-icon:hover {
  transform: scale(1.05);
}

.icon-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.icon-fallback {
  font-size: 1.5rem;
}

/* Metrics Section */
.tile-metrics {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.metric {
  min-width: 0;
}

.metric-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
}

.metric-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.metric-value.passing {
  color: #10b981;
}

.metric-value.failing {
  color: #ef4444;
}

.metric-sublabel {
  font-size: 0.8125rem;
  color: #64748b;
}

/* Status Badge - Repositioned Below Metrics */
.status-badge {
  display: none; /* Hide old absolute positioned badge */
}

.status-badge-bottom {
  display: inline-block;
  padding: 0.375rem 0.875rem;
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
}

.status-badge-bottom.all-pass {
  background-color: #d1fae5;
  color: #065f46;
}

.status-badge-bottom.has-fails {
  background-color: #fee2e2;
  color: #991b1b;
}

.status-badge-bottom.mixed {
  background-color: #fef3c7;
  color: #92400e;
}

/* Progress Bar */
.tile-progress {
  margin-bottom: 1rem;
}

.progress-bar {
  height: 0.5rem;
  background-color: #f1f5f9;
  border-radius: 0.25rem;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  border-radius: 0.25rem;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.progress-fill::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.2) 0%,
    rgba(255, 255, 255, 0) 100%
  );
}

/* Progress Bar Color Classes */
.progress-excellent {
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.3);
}

.progress-moderate {
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.3);
}

.progress-poor {
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.3);
}

/* Compliance Score */
.compliance-score {
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
}

.score-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.score-label {
  font-size: 0.8125rem;
  color: #64748b;
}

/* Action Button */
.tile-action {
  width: 100%;
  padding: 0.625rem 1rem;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  font-family: inherit;
}

.tile-action:hover {
  background-color: #3b82f6;
  color: white;
  border-color: #3b82f6;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);
}

.tile-action:active {
  transform: translateY(0);
}

/* Decorative Element */
.tile-decoration {
  position: absolute;
  top: -3rem;
  right: -3rem;
  width: 6rem;
  height: 6rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 50%;
  opacity: 0.05;
  transition: all 0.3s ease;
}

.stat-tile:hover .tile-decoration {
  transform: scale(1.1);
  opacity: 0.08;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .tile-metrics {
    gap: 1rem;
  }

  .metric-value {
    font-size: 1.5rem;
  }

  .tile-title {
    font-size: 0.9375rem;
  }

  .tile-icon {
    width: 2.5rem;
    height: 2.5rem;
  }
}

@media (max-width: 480px) {
  .tile-metrics {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .stat-tile {
    padding: 1.25rem;
  }
}
</style>
